import { supabase } from '../client';

/**
 * Public preferences for an email address, loadable by unsubscribe token
 * (no auth required). Backs the `/email-prefs/:token` page that recipients
 * reach via the "Unsubscribe" link in any email PeptideClients sends.
 *
 * The token is the credential -- it's a 32-byte random b64url string
 * minted once per address by `core.fn_get_or_create_email_prefs` and is
 * stable forever, so old emails' links keep working.
 */
export interface EmailPrefs {
  email: string;
  unsubscribed_all: boolean;
  unsub_marketing: boolean;
  unsub_operational: boolean;
}

/**
 * Loads preferences by token. Throws on invalid / unknown token; the caller
 * should treat any error as "this link isn't valid -- show the generic
 * not-found page" rather than leaking which condition triggered it.
 */
export async function loadEmailPrefs(token: string): Promise<EmailPrefs> {
  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_email_prefs_load', { p_token: token });

  if (error) throw error;
  // The RPC returns a setof; supabase-js gives us an array back.
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('Preferences not found for this link.');
  return {
    email: row.email,
    unsubscribed_all: Boolean(row.unsubscribed_all),
    unsub_marketing: Boolean(row.unsub_marketing),
    unsub_operational: Boolean(row.unsub_operational),
  };
}

export interface SaveEmailPrefsInput {
  unsubscribed_all: boolean;
  unsub_marketing: boolean;
  unsub_operational: boolean;
}

/**
 * Persists the recipient's choices. The DB function enforces that
 * `unsubscribed_all=true` implies the per-class flags are also true (so
 * the "unsubscribe from everything" master switch can never become
 * inconsistent with individual category flags).
 */
export async function saveEmailPrefs(
  token: string,
  input: SaveEmailPrefsInput,
): Promise<void> {
  const { error } = await supabase()
    .schema('core')
    .rpc('rpc_email_prefs_save', {
      p_token: token,
      p_unsubscribed_all: input.unsubscribed_all,
      p_unsub_marketing: input.unsub_marketing,
      p_unsub_operational: input.unsub_operational,
    });
  if (error) throw error;
}
